import { Injectable } from '@angular/core';
import { StorageService } from 'ngx-lib';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UserOrganizationStorageService } from './user-organization-storage.service';
import { UserStorageService } from './user-storage.service';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface EmptyAdditionalFilters {}

export enum Order {
    asc = 'A',
    desc = 'D'
}

export interface IGridOrderConfig<T> {
    key?: keyof T;
    order?: Order;
    displayName?: string;
}

@Injectable()
export class FilterStateService<M extends object, A extends object = EmptyAdditionalFilters> {
    private readonly filtersSubject = new BehaviorSubject<(M & A) | undefined>(undefined);

    private currentMain: M | undefined;
    private currentAdditional: A | undefined;

    get stateFilters$(): Observable<(M & A) | undefined> {
        return this.filtersSubject.asObservable().pipe(debounceTime(500));
    }

    get current(): (M & A) | undefined {
        return this.filtersSubject.getValue();
    }

    constructor(
        private readonly storageService: StorageService,
        private readonly userOrganizationStorageService: UserOrganizationStorageService,
        private readonly userStorageService: UserStorageService
    ) {
        this.currentMain = undefined;
        this.currentAdditional = undefined;
    }

    updateMainFilters(mainFilters: M): void {
        this.currentMain = mainFilters;
        this.emitMerged();
    }

    updateAdditionalFilters(additionalFilters: A): void {
        this.currentAdditional = additionalFilters;
        this.emitMerged();
    }

    updateFilters(mainFilters: M, additionalFilters?: A): void {
        this.currentMain = mainFilters;

        this.currentAdditional = additionalFilters ?? ({} as A);

        this.emitMerged();
    }

    async clearAllFilters(): Promise<void> {
        this.updateFilters({} as M, {} as A);
    }

    saveToLocalStorage(key: string, filters: M & A): void {
        const finalKey = this.generateFiltersKey(key);
        const previous = this.storageService.getItem(finalKey);

        if (JSON.stringify(filters) !== JSON.stringify(previous)) {
            this.storageService.setItem(finalKey, filters);
        }
    }

    getFromLocalStorage(key: string): (M & A) | undefined {
        const finalKey = this.generateFiltersKey(key);
        const filters = this.storageService.getItem(finalKey) as (M & A) | undefined;
        return filters;
    }

    onOrderChanged(newOrder: IGridOrderConfig<M>): void {
        const newSortBy = newOrder?.displayName;
        const newSortOrder = newOrder?.order;
        const currentMainWithSort = this.currentMain as any;
        if (
            (this.currentMain &&
                (newSortBy !== currentMainWithSort.sortBy || newSortOrder !== currentMainWithSort.sortDirection)) ||
            (!this.currentMain && newOrder)
        ) {
            this.currentMain = {
                ...this.currentMain,
                sortBy: newOrder?.displayName ?? undefined,
                sortDirection: newOrder?.order ?? undefined
            } as M;
            this.emitMerged();
        }
    }

    private emitMerged(): void {
        const merged = { ...this.currentMain, ...this.currentAdditional } as M & A;
        this.filtersSubject.next(merged);
    }

    private generateFiltersKey(key: string): string {
        const userId = this.userStorageService.getUser()?.id;
        const organizationId = this.userOrganizationStorageService.getUserOrganization()?.id;
        return `${key}Filters-user:${userId}-org:${organizationId}`;
    }
}
